import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Trash2, Filter, Search, AlertTriangle, Loader, Sparkles } from "lucide-react";

const STATUS_COLORS = {
  pending_owner_review: "bg-asap-yellow/40 text-asap-deep border-asap-yellow",
  pending: "bg-slate-100 text-slate-700 border-slate-400",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-500",
  en_route: "bg-emerald-100 text-emerald-700 border-emerald-500",
  on_scene: "bg-emerald-100 text-emerald-700 border-emerald-500",
  completed: "bg-blue-100 text-blue-700 border-blue-500",
  cancelled: "bg-red-100 text-red-700 border-red-500",
};

/** Admin Bookings — view all + per-row delete + bulk cleanup (test data scrubbing). */
export default function BookingsManagementPanel() {
  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [cleanupOpen, setCleanupOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } catch (e) { setErr(e.response?.data?.detail || "Failed to load bookings"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = bookings;
    if (status !== "all") list = list.filter((b) => b.status === status);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((b) =>
      (b.service_name || "").toLowerCase().includes(s) ||
      (b.customer_name || "").toLowerCase().includes(s) ||
      (b.driver_name || "").toLowerCase().includes(s) ||
      (b.pickup_address || "").toLowerCase().includes(s) ||
      (b.id || "").toLowerCase().includes(s)
    );
    return list;
  }, [bookings, status, q]);

  const flash = (kind, text) => {
    if (kind === "msg") setMsg(text); else setErr(text);
    setTimeout(() => { setMsg(""); setErr(""); }, 6000);
  };

  const delOne = async (b) => {
    if (b.payment_status === "paid" && !window.confirm(`⚠ This booking is PAID ($${Number(b.price).toFixed(2)}). Deleting will NOT auto-refund the customer — issue a refund in Stripe first if needed. Continue?`)) return;
    if (!window.confirm(`Delete booking "${b.service_name} · ${b.customer_name}"? This cannot be undone.`)) return;
    setBusy(b.id);
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      flash("msg", `Booking deleted.`);
      await load();
    } catch (e) { flash("err", e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); }
  };

  const counts = useMemo(() => ({
    all: bookings.length,
    pending_owner_review: bookings.filter((b) => b.status === "pending_owner_review").length,
    pending: bookings.filter((b) => b.status === "pending").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  }), [bookings]);

  return (
    <div className="space-y-4" data-testid="bookings-management-panel">
      <div className="bg-white border-2 border-asap-deep p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex-1 min-w-full sm:min-w-[220px] flex items-center gap-2 border-2 border-slate-200 px-2 py-1.5">
            <Search size={14} className="text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="bookings-search" placeholder="Search service, customer, driver, address, or ID" className="flex-1 text-sm outline-none min-w-0"/>
          </div>
          <div className="w-full sm:w-auto flex gap-1 flex-wrap sm:contents overflow-x-auto no-scrollbar">
            {["all", "pending_owner_review", "pending", "completed", "cancelled"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`bookings-filter-${s}`} className={`shrink-0 px-2 sm:px-3 py-1.5 border-2 border-asap-deep font-bold uppercase text-[10px] tracking-wider ${status === s ? "bg-asap-deep text-white" : "bg-white text-asap-deep"}`}>
                {s.replace(/_/g, " ")} ({counts[s] || 0})
              </button>
            ))}
          </div>
          <button onClick={() => setCleanupOpen(true)} data-testid="bookings-cleanup-btn" className="w-full sm:w-auto sm:ml-auto px-3 py-1.5 bg-asap-red text-white border-2 border-asap-deep font-bold uppercase text-[10px] tracking-wider inline-flex items-center justify-center gap-1">
            <Sparkles size={12}/> Clean Up Test Data
          </button>
        </div>
      </div>
      {msg && <div className="bg-emerald-50 border-2 border-emerald-500 text-emerald-800 px-4 py-2 font-bold text-sm">{msg}</div>}
      {err && <div className="bg-red-50 border-2 border-asap-red text-asap-red px-4 py-2 font-bold text-sm">{err}</div>}

      <div className="bg-white border-2 border-asap-deep overflow-hidden">
        {/* Mobile: stacked cards. Desktop (md+): full table. */}
        <div className="md:hidden divide-y divide-slate-200" data-testid="bookings-mobile-list">
          {filtered.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No bookings match.</div>}
          {filtered.map((b) => (
            <div key={b.id} className="p-3 space-y-2" data-testid={`booking-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-asap-deep">{b.service_name}</div>
                  <div className="text-xs text-slate-500">
                    <span className="font-bold">{b.customer_name}</span>
                    {b.driver_name && <span> → {b.driver_name}</span>}
                  </div>
                </div>
                <div className="font-display text-asap-orange text-lg shrink-0">${Number(b.price).toFixed(2)}</div>
              </div>
              <div className="flex flex-wrap gap-1 text-[10px]">
                <span className={`uppercase tracking-widest px-1.5 py-0.5 border font-bold ${STATUS_COLORS[b.status] || "border-slate-400 text-slate-600"}`}>{b.status?.replace(/_/g, " ")}</span>
                <span className={`uppercase tracking-widest px-1.5 py-0.5 border font-bold ${b.payment_status === "paid" ? "text-emerald-600 border-emerald-500" : "text-slate-500 border-slate-400"}`}>{b.payment_status}</span>
                <span className="text-slate-400 ml-auto">{b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}</span>
              </div>
              <button onClick={() => delOne(b)} disabled={busy === b.id} data-testid={`booking-delete-${b.id}`} className="w-full px-3 py-2 border-2 border-asap-red text-asap-red font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1 hover:bg-asap-red hover:text-white disabled:opacity-50">
                {busy === b.id ? <Loader size={10} className="animate-spin"/> : <Trash2 size={10}/>} Delete Booking
              </button>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-asap-deep bg-slate-50">
              <tr>
                <th className="py-2 px-3">Service</th>
                <th className="px-3">Customer</th>
                <th className="px-3">Driver</th>
                <th className="px-3">Status</th>
                <th className="px-3">Payment</th>
                <th className="px-3 text-right">Price</th>
                <th className="px-3">Created</th>
                <th className="px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-slate-100" data-testid={`booking-row-${b.id}`}>
                  <td className="py-2 px-3 font-bold text-asap-deep">{b.service_name}</td>
                  <td className="px-3 text-slate-600">{b.customer_name}</td>
                  <td className="px-3 text-slate-600">{b.driver_name || "—"}</td>
                  <td className="px-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border font-bold ${STATUS_COLORS[b.status] || "border-slate-400 text-slate-600"}`}>{b.status?.replace(/_/g, " ")}</span></td>
                  <td className="px-3 text-xs"><span className={`text-[10px] uppercase tracking-widest font-bold ${b.payment_status === "paid" ? "text-emerald-600" : "text-slate-500"}`}>{b.payment_status}</span></td>
                  <td className="px-3 text-right font-display text-asap-orange">${Number(b.price).toFixed(2)}</td>
                  <td className="px-3 text-[10px] text-slate-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 text-right">
                    <button onClick={() => delOne(b)} disabled={busy === b.id} data-testid={`booking-delete-${b.id}`} className="px-2 py-1 border-2 border-asap-red text-asap-red font-bold text-[10px] uppercase inline-flex items-center gap-1 hover:bg-asap-red hover:text-white disabled:opacity-50">
                      {busy === b.id ? <Loader size={10} className="animate-spin"/> : <Trash2 size={10}/>} Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400 text-sm">No bookings match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {cleanupOpen && <CleanupModal onClose={() => setCleanupOpen(false)} onDone={async () => { setCleanupOpen(false); await load(); flash("msg", "Cleanup complete."); }} />}
    </div>
  );
}

function CleanupModal({ onClose, onDone }) {
  const [f, setF] = useState({ status: "", email_contains: "test.com", older_than_days: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const call = async (dry_run) => {
    setBusy(true); setErr(""); setPreview(null);
    try {
      const payload = { dry_run };
      if (f.status) payload.status = f.status;
      if (f.email_contains) payload.email_contains = f.email_contains;
      if (f.older_than_days) payload.older_than_days = parseInt(f.older_than_days, 10);
      const { data } = await api.post("/admin/bookings/cleanup", payload);
      if (dry_run) setPreview(data);
      else { await onDone(); }
    } catch (e) { setErr(e.response?.data?.detail || "Cleanup failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="cleanup-modal" onClick={onClose}>
      <div className="bg-white border-2 border-asap-deep max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 bg-asap-red text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16}/>
            <div className="font-display uppercase text-sm">Bulk Clean-Up Test Data</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-600">
            Match ANY combination below. All filters are AND'd — bookings must satisfy every non-empty condition.
            <strong className="block mt-1 text-asap-red">Always click "Preview" first.</strong>
          </div>
          {err && <div className="bg-red-50 border-2 border-asap-red text-asap-red p-2 text-sm font-bold">{err}</div>}
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Status</div>
            <select value={f.status} onChange={(e) => { setF({ ...f, status: e.target.value }); setPreview(null); }} data-testid="cleanup-status" className="w-full px-3 py-2 border-2 border-asap-deep text-sm bg-white">
              <option value="">any</option>
              <option value="pending_owner_review">pending_owner_review</option>
              <option value="pending">pending</option>
              <option value="cancelled">cancelled</option>
              <option value="completed">completed</option>
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Email contains</div>
            <input value={f.email_contains} onChange={(e) => { setF({ ...f, email_contains: e.target.value }); setPreview(null); }} data-testid="cleanup-email" placeholder="e.g. test.com  or  pqtest" className="w-full px-3 py-2 border-2 border-asap-deep text-sm"/>
            <div className="text-[10px] text-slate-500 mt-1">Matches customer OR driver email containing this substring (case-insensitive).</div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Older than (days)</div>
            <input type="number" min="0" value={f.older_than_days} onChange={(e) => { setF({ ...f, older_than_days: e.target.value }); setPreview(null); }} data-testid="cleanup-days" placeholder="e.g. 30" className="w-full px-3 py-2 border-2 border-asap-deep text-sm"/>
          </label>
          {preview && (
            <div className="bg-asap-yellow/20 border-2 border-asap-yellow p-3 text-sm">
              <strong>Preview:</strong> {preview.matched} booking(s) will be deleted along with their chat messages and location snapshots.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button onClick={onClose} disabled={busy} data-testid="cleanup-cancel" className="px-3 py-2 border-2 border-asap-deep text-asap-deep font-bold text-xs uppercase hover:bg-slate-50">Cancel</button>
            <button onClick={() => call(true)} disabled={busy || (!f.status && !f.email_contains && !f.older_than_days)} data-testid="cleanup-preview" className="px-3 py-2 border-2 border-asap-deep text-asap-deep font-bold text-xs uppercase hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
              {busy ? <Loader size={12} className="animate-spin"/> : <Filter size={12}/>} Preview
            </button>
            <button onClick={() => call(false)} disabled={busy || !preview || preview.matched === 0} data-testid="cleanup-confirm" className="px-4 py-2 bg-asap-red text-white font-bold text-xs uppercase border-2 border-asap-deep inline-flex items-center gap-1 disabled:opacity-50">
              {busy ? <Loader size={12} className="animate-spin"/> : <Trash2 size={12}/>} Delete {preview?.matched || 0}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
